import { createApiClient, type ApiClient } from '@social-studio/api-client';
import { CSRF_HEADER, emitSessionEvent, readCsrfToken } from './session';

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

/**
 * The single place the web app talks to the API (REQ-05-003): the generated,
 * typed client. Same origin in production (Caddy) and in development (Vite
 * proxy), so paths stay relative and the session cookie is sent. Direct
 * `fetch`, `XMLHttpRequest` or `axios` anywhere else fails lint (AC-05-017).
 */
export const api: ApiClient = createApiClient({
  // Absolute, because `Request` rejects relative URLs outside a browser document.
  baseUrl: globalThis.location?.origin ?? '',
  credentials: 'same-origin',
  // Resolved per call, not captured once: keeps the client testable.
  fetch: (request) => globalThis.fetch(request),
});

// CSRF double submit (spec 10 §5): state-changing requests echo the CSRF cookie.
api.use({
  onRequest({ request }) {
    const token = readCsrfToken();
    if (token && !SAFE_METHODS.has(request.method)) {
      request.headers.set(CSRF_HEADER, token);
    }
    return request;
  },
  async onResponse({ request, response }) {
    // A failed sign-in is the sign-in page's own business.
    if (response.status === 401 && !new URL(request.url).pathname.endsWith('/v1/sessions')) {
      emitSessionEvent('unauthenticated');
    } else if (response.status === 403) {
      const problem = (await response.clone().json().catch(() => ({}))) as { type?: string };
      if (problem.type?.endsWith('/tenant-access-denied')) emitSessionEvent('tenant-access-denied');
      else if (problem.type?.endsWith('/tenant-suspended')) emitSessionEvent('tenant-suspended');
    }
    return response;
  },
});

/**
 * The render app's client (spec 90 §4.5): the same generated client, but
 * without cookie, CSRF or session events; the render token passed per call
 * is its only credential.
 */
export function createRenderApiClient(fetchImpl: (request: Request) => Promise<Response> = (request) => globalThis.fetch(request)): ApiClient {
  return createApiClient({ baseUrl: globalThis.location?.origin ?? '', credentials: 'omit', fetch: fetchImpl });
}
